import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Tabs,
  Tab,
  Avatar,
  Badge,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Download,
  Print,
  Share,
  FilterList,
  Search,
  Refresh,
  BarChart,
  PieChart,
  Timeline,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Inventory,
  People,
  Build,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  Add,
  Save,
  LocalShipping
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: '2024-01-01',
    endDate: '2024-01-31'
  });
  const [reportType, setReportType] = useState('daily');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [generatingReport, setGeneratingReport] = useState(false);

  // Mock data for charts and reports
  const productionData = {
    labels: ['Jan 1', 'Jan 2', 'Jan 3', 'Jan 4', 'Jan 5', 'Jan 6', 'Jan 7'],
    datasets: [
      {
        label: 'Soft Soil (m³)',
        data: [1200, 1900, 1500, 2100, 1800, 2200, 2000],
        backgroundColor: '#8FBC8F',
      },
      {
        label: 'Hard Soil (m³)',
        data: [800, 1100, 900, 1200, 1000, 1300, 1100],
        backgroundColor: '#A9A9A9',
      },
      {
        label: 'Coal (tons)',
        data: [500, 700, 600, 800, 650, 850, 750],
        backgroundColor: '#2F4F4F',
      },
    ],
  };

  const costData = {
    labels: ['Labor', 'Equipment', 'Fuel', 'Drill & Blast', 'Logistics', 'Fixed'],
    datasets: [
      {
        label: 'Cost Distribution',
        data: [25, 30, 20, 10, 8, 7],
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40'
        ],
      },
    ],
  };

  const efficiencyData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [
      {
        label: 'Utilization Rate (%)',
        data: [75, 82, 78, 85],
        borderColor: '#36A2EB',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Fuel Efficiency (tons/L)',
        data: [2.5, 2.7, 2.6, 2.8],
        borderColor: '#4BC0C0',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
      },
    ],
  };

  const equipmentData = {
    labels: ['Excavators', 'ADTs', 'Dozers', 'FELs', 'Watercarts'],
    datasets: [
      {
        label: 'Utilization Rate',
        data: [85, 92, 70, 55, 80],
        backgroundColor: '#9966FF',
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Production Report',
      },
    },
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
    },
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const handleGenerateReport = () => {
    setGeneratingReport(true);
    // Simulate API call
    setTimeout(() => {
      setGeneratingReport(false);
      alert('Report generated successfully!');
    }, 2000);
  };

  const handleExportReport = (format) => {
    alert(`Exporting report in ${format.toUpperCase()} format...`);
  };

  const summaryStats = {
    totalProduction: 12500,
    totalCost: 1859000,
    avgCostPerTon: 148.72,
    equipmentUtilization: 78,
    fuelEfficiency: 2.65,
    onTimeDelivery: 92
  };

  const recentReports = [
    { id: 1, name: 'Monthly Production Report - Jan 2024', date: '2024-02-01', type: 'monthly', size: '2.4 MB' },
    { id: 2, name: 'Cost Analysis - S2B8', date: '2024-01-28', type: 'block', size: '1.8 MB' },
    { id: 3, name: 'Equipment Utilization Report', date: '2024-01-25', type: 'equipment', size: '1.2 MB' },
    { id: 4, name: 'Safety & Compliance Report', date: '2024-01-20', type: 'safety', size: '3.1 MB' },
    { id: 5, name: 'Weekly Production Summary', date: '2024-01-15', type: 'weekly', size: '0.9 MB' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reports & Analytics
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            sx={{ mr: 2 }}
            onClick={handleGenerateReport}
            disabled={generatingReport}
          >
            {generatingReport ? 'Generating...' : 'Refresh Data'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Download />}
            onClick={() => handleExportReport('pdf')}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value)}
              >
                <MenuItem value="daily">Daily Report</MenuItem>
                <MenuItem value="weekly">Weekly Report</MenuItem>
                <MenuItem value="monthly">Monthly Report</MenuItem>
                <MenuItem value="quarterly">Quarterly Report</MenuItem>
                <MenuItem value="custom">Custom Report</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Select Block</InputLabel>
              <Select
                value={selectedBlock}
                label="Select Block"
                onChange={(e) => setSelectedBlock(e.target.value)}
              >
                <MenuItem value="all">All Blocks</MenuItem>
                <MenuItem value="S2B8">S2B8</MenuItem>
                <MenuItem value="N1B4">N1B4</MenuItem>
                <MenuItem value="E3B6">E3B6</MenuItem>
                <MenuItem value="W2B3">W2B3</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button startIcon={<FilterList />} variant="outlined">
                More Filters
              </Button>
              <Button 
                startIcon={<BarChart />} 
                variant="contained"
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                Generate Report
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'primary.light', 
                  borderRadius: '50%', 
                  p: 1, 
                  mr: 2 
                }}>
                  <Inventory color="primary" />
                </Box>
                <Typography variant="h6">Total Production</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {summaryStats.totalProduction.toLocaleString()}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                tons/m³ this period
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ArrowUpward color="success" fontSize="small" />
                <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                  +12% from last period
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'error.light', 
                  borderRadius: '50%', 
                  p: 1, 
                  mr: 2 
                }}>
                  <AttachMoney color="error" />
                </Box>
                <Typography variant="h6">Total Cost</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                R {summaryStats.totalCost.toLocaleString()}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                This reporting period
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ArrowDownward color="error" fontSize="small" />
                <Typography variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                  -5% from last period
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'success.light', 
                  borderRadius: '50%', 
                  p: 1, 
                  mr: 2 
                }}>
                  <TrendingUp color="success" />
                </Box>
                <Typography variant="h6">Equipment Utilization</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {summaryStats.equipmentUtilization}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={summaryStats.equipmentUtilization}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
                color={summaryStats.equipmentUtilization > 80 ? "success" : "primary"}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'warning.light', 
                  borderRadius: '50%', 
                  p: 1, 
                  mr: 2 
                }}>
                  <LocalShipping color="warning" />
                </Box>
                <Typography variant="h6">Fuel Efficiency</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {summaryStats.fuelEfficiency}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                tons per liter
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ArrowUpward color="success" fontSize="small" />
                <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                  +8% improvement
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Production Overview</Typography>
              <Box>
                <Tooltip title="Export Chart">
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <Download fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print Chart">
                  <IconButton size="small">
                    <Print fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ height: 300 }}>
              <Bar options={options} data={productionData} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Cost Distribution</Typography>
            <Box sx={{ height: 300 }}>
              <Pie options={pieOptions} data={costData} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Efficiency Trends</Typography>
            <Box sx={{ height: 300 }}>
              <Line options={lineOptions} data={efficiencyData} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Equipment Utilization</Typography>
            <Box sx={{ height: 300 }}>
              <Bar 
                data={equipmentData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Detailed Reports Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Recent Reports</Typography>
          <Box>
            <Button startIcon={<Add />} variant="outlined" sx={{ mr: 1 }}>
              New Report
            </Button>
            <Button startIcon={<Share />} variant="outlined">
              Share All
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Report Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BarChart sx={{ mr: 2, color: 'primary.main' }} />
                      <Typography variant="body2">{report.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{report.date}</TableCell>
                  <TableCell>
                    <Chip label={report.type} size="small" />
                  </TableCell>
                  <TableCell>{report.size}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print">
                      <IconButton size="small">
                        <Print fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Export Options */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Export Options</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                    <Download />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>PDF Export</Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    High-quality printable report with all charts and tables
                  </Typography>
                  <Button 
                    variant="contained" 
                    fullWidth
                    onClick={() => handleExportReport('pdf')}
                  >
                    Export as PDF
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                    <Download />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>Excel Export</Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Raw data in spreadsheet format for further analysis
                  </Typography>
                  <Button 
                    variant="contained" 
                    fullWidth
                    onClick={() => handleExportReport('excel')}
                  >
                    Export as Excel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                    <Share />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>Share Dashboard</Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Share live dashboard access with stakeholders
                  </Typography>
                  <Button 
                    variant="contained" 
                    fullWidth
                    onClick={() => alert('Sharing dashboard...')}
                  >
                    Share Dashboard
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ReportsPage;